import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { Subject, takeUntil } from "rxjs";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { HelperNumericInput } from "@bitwarden/web-vault/app/shared/utils/helper-numeric-input";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { BudgetUtils } from "@bitwarden/web-vault/app/models/view/budget/budget.utils";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { SuggestedBudget } from "@bitwarden/web-vault/app/gloss/plugins/auto-budget/auto-budget.service";

/**
 * Lets the user review, rename, re-amount, or drop individual auto-generated budget
 * suggestions before any budget is actually created. Opened via
 * DialogFactory.openAutoBudgetPreview().
 */
@Component({
  selector: "app-auto-budget-preview-dialog",
  templateUrl: "./auto-budget-preview-dialog.component.html",
})
export class AutoBudgetPreviewDialogComponent implements OnInit, OnDestroy {
  @Input() closeDialog!: CallableFunction;
  /** Tells the host panel when a write is running, so click-away/Escape can't close it mid-write. */
  @Input() onBusyChange?: (busy: boolean) => void;
  @Input() suggestions: SuggestedBudget[] = [];
  /** `markCreated` lets the caller report each budget as it is written, so a partial failure
   *  doesn't get re-created on retry even if the store hasn't refreshed yet. */
  @Input() onConfirm!: (
    finalized: SuggestedBudget[],
    markCreated: (created: SuggestedBudget) => void,
  ) => void | Promise<void>;

  private formBuilder = inject(FormBuilder);
  private budgetStoreService = inject(BudgetStoreService);
  private confirmService: ConfirmationDialogService = inject(ConfirmationDialogService);
  rows: FormArray = new FormArray([]);
  isSubmitting = false;
  /** Indexes of included rows whose name collides; recomputed on form changes, not per CD cycle. */
  duplicateIndexes = new Set<number>();
  private createdCategoryIds = new Set<string>();
  private destroy$ = new Subject<void>();

  readonly minAmount = 0.01;

  ngOnInit(): void {
    this.rows = this.formBuilder.array(
      this.suggestions.map((suggestion) =>
        this.formBuilder.group({
          include: [true],
          name: [suggestion.name, [Validators.required, Validators.pattern(/\S/)]],
          amount: [
            suggestion.monthlyAmount,
            [
              HelperNumericInput.isNumberValidator,
              Validators.required,
              Validators.min(this.minAmount),
            ],
          ],
        }),
      ),
    );
    this.rows.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshDuplicates());
    this.refreshDuplicates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get rowControls(): FormGroup[] {
    return this.rows.controls as FormGroup[];
  }

  get includedCount(): number {
    return this.rowControls.filter((row) => row.value.include).length;
  }

  /**
   * An included row's name collides with an existing budget, or with another included row
   * (case-insensitive, trimmed) — the manual budget form rejects both too.
   */
  private refreshDuplicates(): void {
    const existing = this.budgetStoreService.budget.budgetViews() ?? [];
    const counts = new Map<string, number>();
    const key = (name: string) => (name ?? "").trim().toLowerCase();
    this.rowControls.forEach((r) => {
      if (r.value.include) {
        counts.set(key(r.value.name), (counts.get(key(r.value.name)) ?? 0) + 1);
      }
    });
    this.duplicateIndexes = new Set(
      this.rowControls.flatMap((row, i) =>
        row.value.include &&
        (BudgetUtils.isNameDuplicate(row.value.name, existing) ||
          counts.get(key(row.value.name)) > 1)
          ? [i]
          : [],
      ),
    );
  }

  isDuplicateName(index: number): boolean {
    return this.duplicateIndexes.has(index);
  }

  async confirm(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }
    this.rowControls.forEach((row) => {
      if (row.value.include) {
        row.markAllAsTouched();
      }
    });

    this.refreshDuplicates();
    const hasInvalidIncludedRow = this.rowControls.some(
      (row, index) => row.value.include && (row.invalid || this.isDuplicateName(index)),
    );
    if (hasInvalidIncludedRow) {
      return;
    }

    const finalized: SuggestedBudget[] = [];
    this.rowControls.forEach((row, index) => {
      if (!row.value.include) {
        return;
      }
      finalized.push({
        categories: this.suggestions[index].categories,
        name: row.value.name.trim(),
        monthlyAmount: Math.round(Number(row.value.amount) * 100) / 100,
      });
    });

    this.isSubmitting = true;
    this.onBusyChange?.(true);
    this.createdCategoryIds.clear();
    let succeeded = false;
    try {
      await this.onConfirm(finalized, (created) =>
        created.categories.forEach((c) => this.createdCategoryIds.add(c.id)),
      );
      succeeded = true;
    } catch (error) {
      // keep the dialog open so the user can retry; surface the failure here since
      // the caller doesn't handle it. Drop rows whose budget was created before the failure,
      // otherwise a retry would create them a second time.
      this.removeCreatedRows();
      // eslint-disable-next-line no-console
      console.error("Failed to create auto-generated budgets", error);
      this.confirmService.notify(NotificationEnum.error, "error", "errorOccurred");
    } finally {
      this.isSubmitting = false;
      this.onBusyChange?.(false);
    }
    if (succeeded || this.rowControls.length === 0) {
      this.cancel();
    }
  }

  /** Suggestions only hold unbudgeted categories, so a row whose categories are now budgeted was
   *  committed by the partial write. Matches on category ids, not names, which the user can edit. */
  private removeCreatedRows(): void {
    const budgeted = new Set([
      ...this.createdCategoryIds,
      ...(this.budgetStoreService.budget.budgetViews() ?? []).flatMap((b) =>
        BudgetUtils.getCategoryIdsInBudget(b),
      ),
    ]);
    for (let i = this.rowControls.length - 1; i >= 0; i--) {
      if (this.suggestions[i].categories.some((c) => budgeted.has(c.id))) {
        this.rows.removeAt(i);
        this.suggestions = this.suggestions.filter((_, idx) => idx !== i);
      }
    }
    this.refreshDuplicates();
  }

  cancel(): void {
    // closing mid-write would orphan the in-flight onConfirm
    if (this.isSubmitting) {
      return;
    }
    if (typeof this.closeDialog === "function") {
      this.closeDialog();
    }
  }
}
